import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, GripVertical, LogOut, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgSrc = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;
const TOKEN_KEY = "vh_admin_token";

const fmtErr = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [years, setYears] = useState([]);
  const [selYear, setSelYear] = useState(null);
  const [selCategory, setSelCategory] = useState(null);
  const [newYear, setNewYear] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [photos, setPhotos] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [confirmCat, setConfirmCat] = useState(null);
  const [adm, setAdm] = useState(null);
  const posterRef = useRef(null);
  const dragIdx = useRef(null);
  const fileRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const loadYears = useCallback(async () => {
    const r = await fetch(`${API}/gallery/years`);
    const data = await r.json();
    setYears(data);
    return data;
  }, []);

  const loadPhotos = useCallback(async (year, name) => {
    const r = await fetch(
      `${API}/gallery/photos?year=${year}&category=${encodeURIComponent(name)}`
    );
    setPhotos(await r.json());
  }, []);

  const loadAdmissions = useCallback(async () => {
    const r = await fetch(`${API}/admissions`);
    setAdm(await r.json());
  }, []);

  useEffect(() => {
    if (token) {
      loadYears();
      loadAdmissions();
    }
  }, [token, loadYears, loadAdmissions]);

  useEffect(() => {
    if (selYear && selCategory) loadPhotos(selYear, selCategory);
    else setPhotos([]);
  }, [selYear, selCategory, loadPhotos]);

  useEffect(() => {
    const yd = years.find((y) => y.year === selYear);
    setOrderList((yd?.categories || []).map((c) => ({ ...c })));
  }, [selYear, years]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("password", password);
      const r = await fetch(`${API}/admin/login`, { method: "POST", body: fd });
      const data = await r.json();
      if (!r.ok) {
        setError(fmtErr(data.detail));
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const authed = async (url, formData) => {
    formData.append("token", token);
    const r = await fetch(url, { method: "POST", body: formData });
    if (r.status === 401) {
      logout();
      throw new Error("Session expired. Please log in again.");
    }
    return r;
  };

  const addYear = () => {
    const y = parseInt(newYear, 10);
    if (!y || y < 1990 || y > 2100) {
      setNotice({ type: "error", text: "Enter a valid year (e.g. 2026)." });
      return;
    }
    setSelYear(y);
    setSelCategory(null);
    setNewYear("");
    setNotice({ type: "ok", text: `Year ${y} selected — now add or pick a category for it.` });
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!selYear) {
      setNotice({ type: "error", text: "Select a year first." });
      return;
    }
    if (!name) {
      setNotice({ type: "error", text: "Enter a category name." });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("year", String(selYear));
      fd.append("name", name);
      const r = await authed(`${API}/admin/categories`, fd);
      const data = await r.json();
      if (!r.ok) {
        setNotice({ type: "error", text: fmtErr(data.detail) });
        return;
      }
      await loadYears();
      setSelCategory(name);
      setNewCategory("");
      setNotice({ type: "ok", text: `Category "${name}" created for ${selYear}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async () => {
    const files = Array.from(fileRef.current?.files || []);
    if (!selYear || !selCategory) {
      setNotice({ type: "error", text: "Select a year and category before uploading." });
      return;
    }
    if (!files.length) {
      setNotice({ type: "error", text: "Choose one or more images first." });
      return;
    }
    setBusy(true);
    let okCount = 0;
    let lastError = null;
    for (const file of files) {
      const fd = new FormData();
      fd.append("year", String(selYear));
      fd.append("category", selCategory);
      fd.append("file", file);
      try {
        const r = await authed(`${API}/admin/upload`, fd);
        const data = await r.json();
        if (!r.ok) lastError = fmtErr(data.detail);
        else okCount += 1;
      } catch (err) {
        lastError = err.message;
      }
    }
    if (fileRef.current) fileRef.current.value = "";
    await loadPhotos(selYear, selCategory);
    await loadYears();
    if (lastError) setNotice({ type: "error", text: lastError });
    else setNotice({ type: "ok", text: `${okCount} photo${okCount === 1 ? "" : "s"} uploaded.` });
    setBusy(false);
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      const r = await authed(`${API}/admin/photos/${id}/delete`, fd);
      if (!r.ok) {
        const data = await r.json();
        setNotice({ type: "error", text: fmtErr(data.detail) });
        return;
      }
      await loadPhotos(selYear, selCategory);
      await loadYears();
      setNotice({ type: "ok", text: "Photo deleted." });
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveOrder = async (list) => {
    setSavingOrder(true);
    try {
      const fd = new FormData();
      fd.append("year", String(selYear));
      list.forEach((c) => fd.append("names", c.name));
      const r = await authed(`${API}/admin/categories/reorder`, fd);
      const data = await r.json();
      if (!r.ok) {
        setNotice({ type: "error", text: fmtErr(data.detail) });
        await loadYears();
        return;
      }
      setNotice({ type: "ok", text: "Category order saved." });
      await loadYears();
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    } finally {
      setSavingOrder(false);
    }
  };

  const moveCategory = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= orderList.length) return;
    const next = [...orderList];
    [next[i], next[j]] = [next[j], next[i]];
    setOrderList(next);
    saveOrder(next);
  };

  const handleDrop = (target) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === target) return;
    const next = [...orderList];
    const [moved] = next.splice(from, 1);
    next.splice(target, 0, moved);
    setOrderList(next);
    saveOrder(next);
  };

  const confirmDeleteCategory = async () => {
    if (!confirmCat) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("year", String(selYear));
      fd.append("name", confirmCat.name);
      const r = await authed(`${API}/admin/categories/delete`, fd);
      const data = await r.json();
      if (!r.ok) {
        setNotice({ type: "error", text: fmtErr(data.detail) });
        return;
      }
      if (selCategory === confirmCat.name) setSelCategory(null);
      setNotice({ type: "ok", text: `${confirmCat.name} and its photos were deleted.` });
      setConfirmCat(null);
      await loadYears();
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const toggleAdmissions = async () => {
    if (!adm) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("show", adm.show_poster ? "false" : "true");
      const r = await authed(`${API}/admin/admissions/toggle`, fd);
      const data = await r.json();
      if (!r.ok) {
        setNotice({ type: "error", text: fmtErr(data.detail) });
        return;
      }
      await loadAdmissions();
      setNotice({
        type: "ok",
        text: data.show_poster
          ? "Admissions poster is now live on the public page."
          : "Admissions poster hidden — the public page shows the closed message.",
      });
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const uploadPoster = async () => {
    const file = posterRef.current?.files?.[0];
    if (!file) {
      setNotice({ type: "error", text: "Choose a poster image first." });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authed(`${API}/admin/admissions/poster`, fd);
      const data = await r.json();
      if (!r.ok) {
        setNotice({ type: "error", text: fmtErr(data.detail) });
        return;
      }
      if (posterRef.current) posterRef.current.value = "";
      await loadAdmissions();
      setNotice({ type: "ok", text: "Poster uploaded." });
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const activeYearData = years.find((y) => y.year === selYear);
  const categories = activeYearData?.categories || [];

  if (!token) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center bg-[#0A2647] px-6">
        <span className="block h-16 w-16 overflow-hidden rounded-full ring-1 ring-white/30">
          <img
            src="/logo.png"
            alt="Val-Hue logo"
            className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
          />
        </span>
        <h1 className="mt-6 font-display text-3xl font-semibold text-white">
          Gallery Admin
        </h1>
        <p className="mt-2 text-xs uppercase tracking-[0.3em] text-[#D6EBEE]/60">
          Staff access only
        </p>
        <form onSubmit={login} className="mt-8 w-full max-w-xs" data-testid="admin-login-form">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Shared password"
            data-testid="admin-password-input"
            aria-label="Shared password"
            className="w-full rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/40 outline-none backdrop-blur transition-colors focus:border-[#F47221]"
          />
          {error && (
            <p className="mt-3 text-center text-xs text-[#F47221]" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="mt-4 w-full rounded-lg bg-[#F47221] px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Checking…" : "Log in"}
          </button>
        </form>
        <Link
          to="/"
          data-testid="admin-back-link"
          className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-[#D6EBEE]/60 transition-colors hover:text-white"
        >
          Back to site
        </Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F2F8F9]">
      <div className="border-b border-[#143A66]/10 bg-[#0A2647]">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="block h-9 w-9 overflow-hidden rounded-full ring-1 ring-white/30">
              <img
                src="/logo.png"
                alt=""
                className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
              />
            </span>
            <p className="font-display text-sm font-semibold tracking-[0.14em] text-white">
              GALLERY ADMIN
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/gallery"
              data-testid="admin-view-gallery-link"
              className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D6EBEE]/70 transition-colors hover:text-white"
            >
              View gallery
            </Link>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-white/80 transition-colors hover:border-[#F47221] hover:text-[#F47221]"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-dashboard">
        {notice && (
          <p
            data-testid="admin-notice"
            className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
              notice.type === "error"
                ? "border-[#F47221]/40 bg-[#F47221]/10 text-[#c4531a]"
                : "border-[#143A66]/20 bg-[#D6EBEE]/50 text-[#143A66]"
            }`}
          >
            {notice.text}
          </p>
        )}

        <section>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F47221]">
            1 · Year
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {years.map((y) => (
              <button
                key={y.year}
                type="button"
                onClick={() => {
                  setSelYear(y.year);
                  setSelCategory(null);
                }}
                data-testid={`admin-year-pill-${y.year}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                  selYear === y.year
                    ? "border-[#143A66] bg-[#143A66] text-white"
                    : "border-[#143A66]/20 bg-white text-[#143A66]/70 hover:border-[#F47221]"
                }`}
              >
                {y.year}
              </button>
            ))}
            <input
              type="number"
              value={newYear}
              onChange={(e) => setNewYear(e.target.value)}
              placeholder="New year"
              data-testid="admin-new-year-input"
              className="w-28 rounded-lg border border-[#143A66]/20 bg-white px-3 py-1.5 text-sm text-[#143A66] outline-none focus:border-[#F47221]"
            />
            <button
              type="button"
              onClick={addYear}
              data-testid="admin-add-year-button"
              className="rounded-lg bg-[#143A66] px-4 py-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Add year
            </button>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F47221]">
            2 · Event category
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => setSelCategory(c.name)}
                data-testid={`admin-category-pill-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                  selCategory === c.name
                    ? "border-[#143A66] bg-[#143A66] text-white"
                    : "border-[#143A66]/20 bg-white text-[#143A66]/70 hover:border-[#F47221]"
                }`}
              >
                {c.name} ({c.count}/8)
              </button>
            ))}
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="New category"
              data-testid="admin-new-category-input"
              className="w-44 rounded-lg border border-[#143A66]/20 bg-white px-3 py-1.5 text-sm text-[#143A66] outline-none focus:border-[#F47221]"
            />
            <button
              type="button"
              onClick={addCategory}
              disabled={busy}
              data-testid="admin-add-category-button"
              className="rounded-lg bg-[#143A66] px-4 py-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              Add category
            </button>
          </div>

          {selYear && orderList.length > 0 && (
            <div className="mt-5 max-w-xl" data-testid="category-reorder-list">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#143A66]/50">
                Drag to reorder{savingOrder ? " · saving…" : ""} — changes appear
                on the public gallery
              </p>
              <div className="rounded-xl border border-[#143A66]/15 bg-white p-1.5">
                {orderList.map((c, i) => (
                  <div
                    key={c.name}
                    draggable
                    onDragStart={() => {
                      dragIdx.current = i;
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(i)}
                    data-testid={`reorder-row-${i}`}
                    className={`flex items-center gap-1.5 rounded-lg px-2 py-2 ${
                      selCategory === c.name ? "bg-[#D6EBEE]/50" : ""
                    }`}
                  >
                    <GripVertical
                      className="h-4 w-4 shrink-0 cursor-grab text-[#143A66]/30 active:cursor-grabbing"
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate text-sm font-medium text-[#143A66]">
                      {c.name}
                      <span className="ml-1.5 text-xs text-[#143A66]/40">
                        ({c.count}/8)
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => moveCategory(i, -1)}
                      disabled={i === 0 || savingOrder}
                      data-testid={`move-up-${i}`}
                      aria-label={`Move ${c.name} up`}
                      className="grid h-7 w-7 place-items-center rounded-md text-[#143A66]/50 transition-colors hover:bg-[#D6EBEE]/60 hover:text-[#143A66] disabled:opacity-25"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCategory(i, 1)}
                      disabled={i === orderList.length - 1 || savingOrder}
                      data-testid={`move-down-${i}`}
                      aria-label={`Move ${c.name} down`}
                      className="grid h-7 w-7 place-items-center rounded-md text-[#143A66]/50 transition-colors hover:bg-[#D6EBEE]/60 hover:text-[#143A66] disabled:opacity-25"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmCat(c)}
                      data-testid={`remove-category-${i}`}
                      aria-label={`Remove ${c.name}`}
                      className="grid h-7 w-7 place-items-center rounded-md text-[#143A66]/50 transition-colors hover:bg-[#F47221]/10 hover:text-[#F47221]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="mt-10">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F47221]">
            3 · Photos
            {selCategory && (
              <span className="ml-3 text-[#143A66]/60" data-testid="admin-photo-count">
                {photos.length} / 8 in {selCategory} {selYear}
              </span>
            )}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileRef}
              data-testid="admin-file-input"
              className="text-sm text-[#143A66]/70 file:mr-3 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#143A66] file:ring-1 file:ring-[#143A66]/20"
            />
            <button
              type="button"
              onClick={uploadFiles}
              disabled={busy}
              data-testid="admin-upload-button"
              className="flex items-center gap-2 rounded-lg bg-[#F47221] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <Upload className="h-4 w-4" /> {busy ? "Working…" : "Upload"}
            </button>
          </div>

          {selCategory && photos.length > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-photo-grid">
              {photos.map((p, i) => (
                <div
                  key={p.id}
                  className="group relative aspect-square overflow-hidden rounded-xl ring-1 ring-[#143A66]/10"
                >
                  <img
                    src={imgSrc(p.url)}
                    alt={`Upload ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => deletePhoto(p.id)}
                    data-testid={`admin-delete-${i}`}
                    aria-label={`Delete photo ${i + 1}`}
                    className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-[#0A2647]/80 text-white opacity-90 transition-colors hover:bg-[#F47221]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {selCategory && photos.length === 0 && (
            <p className="mt-6 text-sm text-[#143A66]/50">
              No photos yet in this category — upload up to 8.
            </p>
          )}
          {!selCategory && (
            <p className="mt-6 text-sm text-[#143A66]/50">
              Select a year and category to manage its photos.
            </p>
          )}
        </section>

        <section className="mt-10">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F47221]">
            4 · Admissions
          </h2>
          <div className="mt-3 max-w-xl rounded-xl border border-[#143A66]/15 bg-white p-5">
            <div className="flex items-center gap-4">
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(adm?.show_poster)}
                onClick={toggleAdmissions}
                disabled={busy || !adm}
                data-testid="admissions-toggle"
                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 disabled:opacity-50 ${
                  adm?.show_poster ? "bg-[#F47221]" : "bg-[#143A66]/25"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all duration-300 ${
                    adm?.show_poster ? "left-[22px]" : "left-0.5"
                  }`}
                />
              </button>
              <div>
                <p className="text-sm font-semibold text-[#143A66]">
                  Show admissions poster
                </p>
                <p
                  className="text-xs text-[#143A66]/60"
                  data-testid="admissions-status"
                >
                  {!adm
                    ? "Loading…"
                    : adm.show_poster
                      ? "On — poster is live on the public page"
                      : "Off — the public page shows the closed message"}
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[#143A66]/10 pt-5">
              <input
                type="file"
                accept="image/*"
                ref={posterRef}
                data-testid="admissions-file-input"
                className="text-sm text-[#143A66]/70 file:mr-3 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#143A66] file:ring-1 file:ring-[#143A66]/20"
              />
              <button
                type="button"
                onClick={uploadPoster}
                disabled={busy}
                data-testid="admissions-upload-button"
                className="flex items-center gap-2 rounded-lg bg-[#F47221] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                <Upload className="h-4 w-4" /> Upload poster
              </button>
            </div>

            {adm?.poster_url && (
              <div className="mt-5">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#143A66]/50">
                  Current poster
                </p>
                <img
                  src={imgSrc(adm.poster_url)}
                  alt="Current admissions poster"
                  data-testid="admissions-poster-preview"
                  className="max-h-48 w-auto rounded-lg ring-1 ring-[#143A66]/15"
                />
              </div>
            )}
          </div>
        </section>
      </div>

      {confirmCat && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0A2647]/80 px-6"
          data-testid="category-delete-dialog"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="font-display text-xl font-semibold text-[#143A66]">
              Delete {confirmCat.name}?
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#143A66]/70">
              This will permanently remove this event category and all photos
              associated with it. This action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmCat(null)}
                data-testid="category-delete-cancel"
                className="rounded-lg border border-[#143A66]/20 px-4 py-2 text-sm font-semibold text-[#143A66] transition-colors hover:border-[#143A66]/50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteCategory}
                disabled={busy}
                data-testid="category-delete-confirm"
                className="rounded-lg bg-[#F47221] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                Delete Category
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
